import { collection, getDocs, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import { auth, db } from '../firebase';

const styles = {
  screen: { minHeight: '100vh', backgroundColor: '#F4F6F9', fontFamily: 'system-ui, sans-serif' },
  main: { marginLeft: 220, padding: 24 },
  pageTitle: { fontSize: 22, fontWeight: '700', color: '#0A2D5A', marginBottom: 4 },
  pageSub: { fontSize: 13, color: '#888', marginBottom: 24 },
  form: { backgroundColor: 'white', borderRadius: 12, border: '0.5px solid #e0e0e0', padding: 24, maxWidth: 600 },
  sectionTitle: { fontSize: 13, fontWeight: '700', color: '#0A2D5A', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #eee' },
  field: { marginBottom: 16 },
  label: { display: 'block', fontSize: 12, fontWeight: '600', color: '#555', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 },
  recipientGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 16 },
  recipientBtn: { padding: '8px', borderRadius: 8, border: '1.5px solid #ddd', fontSize: 13, fontWeight: '600', cursor: 'pointer', backgroundColor: 'white', color: '#888', textAlign: 'center' },
  recipientBtnActive: { padding: '8px', borderRadius: 8, border: '1.5px solid #1D6FA4', fontSize: 13, fontWeight: '600', cursor: 'pointer', backgroundColor: '#E6F1F9', color: '#1D6FA4', textAlign: 'center' },
  clientList: { border: '1px solid #eee', borderRadius: 8, maxHeight: 240, overflowY: 'auto', marginBottom: 16 },
  clientItem: { display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: '0.5px solid #f0f0f0', gap: 10, cursor: 'pointer' },
  emptyNote: { padding: 16, textAlign: 'center', color: '#888', fontSize: 13 },
  infoNote: { fontSize: 12, color: '#888', marginBottom: 16, lineHeight: 1.5 },
  submitBtn: { backgroundColor: '#E8620A', color: 'white', border: 'none', borderRadius: 8, padding: '12px 24px', fontSize: 15, fontWeight: '600', cursor: 'pointer', marginTop: 8 },
  submitBtnDisabled: { backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: 8, padding: '12px 24px', fontSize: 15, fontWeight: '600', cursor: 'not-allowed', marginTop: 8 },
  success: { backgroundColor: '#EAF3DE', color: '#3B6D11', padding: '12px 16px', borderRadius: 8, fontSize: 14, marginBottom: 16, fontWeight: '600' },
  error: { backgroundColor: '#FEE2E2', color: '#CC0000', padding: '12px 16px', borderRadius: 8, fontSize: 14, marginBottom: 16, fontWeight: '600' },
};

export default function SendClientLinks() {
  const [clients, setClients] = useState([]);
  const [recipients, setRecipients] = useState('all');
  const [selectedClients, setSelectedClients] = useState([]);
  const [resultMessage, setResultMessage] = useState('');
  const [resultIsError, setResultIsError] = useState(false);
  const [loading, setLoading] = useState(false);

  const agentId = auth.currentUser?.uid;

  useEffect(() => {
    const fetchClients = async () => {
      if (!agentId) return;
      const q = query(collection(db, 'clients'), where('agentId', '==', agentId));
      const snap = await getDocs(q);
      setClients(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };
    fetchClients();
  }, [agentId]);

  // Only clients who haven't been emailed yet AND have an email on file.
  // Note: this can include clients already queued from an earlier click —
  // the backend catches that and reports it as skippedAlreadyQueued.
  const pendingWithEmail = clients.filter(c => !c.linkEmailedAt && c.email && c.email.trim());
  const pendingNoEmail = clients.filter(c => !c.linkEmailedAt && (!c.email || !c.email.trim()));

  const toggleClient = (id) => {
    setSelectedClients(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const getRecipientCount = () => {
    if (recipients === 'all') return pendingWithEmail.length;
    if (recipients === 'selected') return selectedClients.length;
    return 0;
  };

  const handleSend = async () => {
    if (getRecipientCount() === 0) return;
    setLoading(true);
    setResultMessage('');
    try {
      const functions = getFunctions();
      const queueEmails = httpsCallable(functions, 'queueClientEmails');
      const result = await queueEmails({
        target: recipients,
        selectedClientIds: selectedClients,
      });
      const { queued, skippedNoEmail, skippedAlreadyQueued } = result.data;
      let msg = `Queued ${queued} client${queued !== 1 ? 's' : ''} for delivery.`;
      if (skippedAlreadyQueued > 0) msg += ` ${skippedAlreadyQueued} ${skippedAlreadyQueued !== 1 ? 'were' : 'was'} already queued from an earlier send.`;
      if (skippedNoEmail > 0) msg += ` ${skippedNoEmail} skipped — no email on file.`;
      setResultIsError(false);
      setResultMessage(msg);
      setSelectedClients([]);
      // Refresh the client list. Note: queued clients still show as
      // "pending" here until their email actually goes out and
      // linkEmailedAt is set — that's expected, not a bug.
      const q = query(collection(db, 'clients'), where('agentId', '==', agentId));
      const snap = await getDocs(q);
      setClients(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      console.error('Queue client links error:', err);
      setResultIsError(true);
      setResultMessage('Error queuing emails. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.screen}>
      <Sidebar />
      <div style={styles.main}>
        <div style={styles.pageTitle}>Send Client Links</div>
        <div style={styles.pageSub}>Queue the app link and setup code to be emailed to clients who haven't received it yet. Emails go out automatically over the next several hours, timed to each client's own local business hours.</div>

        <div style={styles.form}>
          {resultMessage && (
            <div style={resultIsError ? styles.error : styles.success}>{resultMessage}</div>
          )}

          <div style={styles.sectionTitle}>Send To</div>
          <div style={styles.recipientGrid}>
            {['all', 'selected'].map(r => (
              <button key={r} type="button"
                style={recipients === r ? styles.recipientBtnActive : styles.recipientBtn}
                onClick={() => setRecipients(r)}>
                {r === 'all' ? 'All Clients' : 'Select Clients'}
              </button>
            ))}
          </div>

          {recipients === 'selected' && (
            <div style={styles.field}>
              <label style={styles.label}>Select Clients</label>
              <div style={styles.clientList}>
                {pendingWithEmail.length === 0 && (
                  <div style={styles.emptyNote}>No clients are waiting on their initial email.</div>
                )}
                {pendingWithEmail.map(client => (
                  <div key={client.id} style={styles.clientItem}
                    onClick={() => toggleClient(client.id)}>
                    <input type="checkbox" readOnly
                      checked={selectedClients.includes(client.id)} />
                    <span style={{ fontSize: 13, color: '#333' }}>{client.name}</span>
                    <span style={{ fontSize: 11, color: '#888', marginLeft: 'auto' }}>{client.email}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={styles.infoNote}>
            {pendingWithEmail.length} client{pendingWithEmail.length !== 1 ? 's' : ''} waiting on their initial email.
            {pendingNoEmail.length > 0 && ` ${pendingNoEmail.length} more ${pendingNoEmail.length !== 1 ? 'have' : 'has'} no email on file and will be skipped.`}
          </div>

          <button
            style={getRecipientCount() > 0 && !loading ? styles.submitBtn : styles.submitBtnDisabled}
            onClick={handleSend}
            disabled={getRecipientCount() === 0 || loading}>
            {loading ? 'Queuing...' : `Queue ${getRecipientCount()} Client${getRecipientCount() !== 1 ? 's' : ''} for Delivery`}
          </button>
        </div>
      </div>
    </div>
  );
}
